import React, { useState } from "react";
import styled from "styled-components";
import { RebelColors, RebelModal, RebelModalHeader, RebelModalBody, RebelModalFooter, RebelAccentButton, RebelSecondaryButton, RebelLabel, RebelInput } from "../../../styles/rebel-experiences";
import { generateGUID } from "../../../utils/database/firebase-budget-tool";
import { generateBudgetSlug } from "../../../utils/slug-generator";
import { setDocument, updateDocumentProperty } from "../../../utils/firestore-functions";
import emailjs from "@emailjs/browser";
import firebase from "firebase/compat/app";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const EMAILJS_SERVICE_ID = "service_xiqdcy8";
const EMAILJS_TEMPLATE_ID = "template_ozs41kd";
const EMAILJS_PUBLIC_KEY = "user_8Qt3ZJv9dlnGhiJefkhBh";

/** Capitalize each word: "john smith" → "John Smith" */
const capitalizeName = (name) =>
    (name || "").trim().replace(/\b\w/g, c => c.toUpperCase());

const RebelBudgetSaveModal = ({ isOpen, toggle, config, properties, rankings, budgetId, setBudgetId, budgetDB, setSavedEmail, setSavedContactName }) => {
    const [email, setEmail] = useState("");
    const [contactName, setContactName] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [savedSuccess, setSavedSuccess] = useState(false);
    const [savedLink, setSavedLink] = useState("");

    const handleSave = async () => {
        if (!email || !email.includes("@")) {
            alert("Please enter a valid email address.");
            return;
        }

        if (!contactName.trim()) {
            alert("Please enter your name.");
            return;
        }

        setIsSaving(true);

        try {
            const docId = budgetId || generateBudgetSlug(config.companyName);
            const now = firebase.firestore.Timestamp.now();

            const data = {
                id: docId,
                email: email,
                contactName: contactName.trim(),
                companyName: config.companyName,
                startDate: config.startDate,
                endDate: config.endDate,
                attendees: config.attendees,
                currency: config.currency,
                properties: properties,
                rankings: rankings || {},
                updatedAt: now,
            };

            if (!budgetId) {
                data.createdAt = now;
            }

            if (budgetId) {
                // Update existing document
                Object.entries(data).forEach(([key, value]) => {
                    updateDocumentProperty(budgetDB, "rebelClientBudgets", docId, key, value);
                });
            } else {
                // Create new document
                await setDocument(budgetDB, "rebelClientBudgets", docId, data);
            }

            // Update URL without reload — use short /r/ URL
            const baseUrl = window.location.origin;
            const resumeLink = `${baseUrl}/r/${docId}`;
            window.history.replaceState(null, "", `/r/${docId}`);
            setBudgetId(docId);
            setSavedLink(resumeLink);

            // Send email
            try {
                const generatedDate = new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

                await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
                    to_email: email,
                    to_name: capitalizeName(contactName),
                    contact_name: capitalizeName(contactName),
                    company_name: config.companyName || "Your Team",
                    start_date: config.startDate || "TBD",
                    end_date: config.endDate || "TBD",
                    attendees: config.attendees,
                    num_properties: properties.length,
                    resume_link: resumeLink,
                    generated_date: generatedDate,
                }, EMAILJS_PUBLIC_KEY);
            } catch (emailError) {
                console.warn("Email sending failed (template may not be configured yet):", emailError);
            }

            if (setSavedEmail) setSavedEmail(email);
            if (setSavedContactName) setSavedContactName(contactName.trim());
            setSavedSuccess(true);
            setIsSaving(false);
        } catch (error) {
            console.error("Error saving budget:", error);
            alert("Failed to save. Please try again.");
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setSavedSuccess(false);
        setSavedLink("");
        toggle();
    };

    const copyLink = () => {
        navigator.clipboard.writeText(savedLink);
    };

    if (savedSuccess) {
        return (
            <RebelModal isOpen={isOpen} toggle={handleClose}>
                <RebelModalHeader>Saved Successfully</RebelModalHeader>
                <RebelModalBody>
                    <SuccessMessage>
                        <FontAwesomeIcon icon={['fas', 'check-circle']} style={{ marginRight: 8 }} />
                        Your budget comparison has been saved!
                    </SuccessMessage>
                    <LinkContainer>
                        <RebelLabel>Your resume link:</RebelLabel>
                        <LinkDisplay>{savedLink}</LinkDisplay>
                        <CopyButton onClick={copyLink}>
                            <FontAwesomeIcon icon={['fas', 'copy']} style={{ marginRight: 6 }} />
                            Copy Link
                        </CopyButton>
                    </LinkContainer>
                    <InfoNote>
                        <FontAwesomeIcon icon={['fas', 'info-circle']} style={{ marginRight: 6 }} />
                        This link is your personal editable version. Any changes you make will automatically reflect for your team. Keep this link safe — it gives full editing access to your budget comparison.
                    </InfoNote>
                    <EmailNote>
                        A copy has been sent to <strong>{contactName}</strong> at <strong>{email}</strong>
                    </EmailNote>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={handleClose}>Done</RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>
        );
    }

    return (
        <RebelModal isOpen={isOpen} toggle={handleClose}>
            <RebelModalHeader>Save & Share</RebelModalHeader>
            <RebelModalBody>
                <p style={{ color: RebelColors.textMuted, marginBottom: '1rem' }}>
                    Save your property comparison and receive a link to resume later.
                </p>
                <FormGroup>
                    <RebelLabel>Your Name</RebelLabel>
                    <RebelInput
                        type="text"
                        value={contactName}
                        onChange={e => setContactName(e.target.value)}
                        placeholder="Enter your full name"
                    />
                </FormGroup>
                <FormGroup>
                    <RebelLabel>Email Address</RebelLabel>
                    <RebelInput
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="your.email@company.com"
                    />
                </FormGroup>
            </RebelModalBody>
            <RebelModalFooter>
                <RebelSecondaryButton onClick={handleClose}>Cancel</RebelSecondaryButton>
                <RebelAccentButton onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "Saving..." : budgetId ? "Update & Send" : "Save & Send"}
                </RebelAccentButton>
            </RebelModalFooter>
        </RebelModal>
    );
};

export default RebelBudgetSaveModal;

const FormGroup = styled.div`
    margin-bottom: 1rem;
`;

const SuccessMessage = styled.p`
    color: ${RebelColors.success};
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LinkContainer = styled.div`
    margin-bottom: 1rem;
`;

const LinkDisplay = styled.div`
    background-color: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.gold};
    border-radius: 6px;
    padding: 10px 12px;
    color: ${RebelColors.gold};
    font-size: 0.85rem;
    word-break: break-all;
    margin-bottom: 0.5rem;
`;

const CopyButton = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;

    &:hover {
        background: ${RebelColors.gold};
        color: #000;
    }
`;

const InfoNote = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
    text-align: left;
    margin: 1rem 0;
    padding: 10px 14px;
    background: rgba(253, 181, 6, 0.05);
    border: 1px solid rgba(253, 181, 6, 0.15);
    border-radius: 8px;
    line-height: 1.4;
`;

const EmailNote = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
    text-align: center;

    strong {
        color: ${RebelColors.text};
    }
`;
